import { AnyAction } from 'redux';
import { PersistConfig, persistReducer } from 'redux-persist';
import { PersistPartial } from 'redux-persist/es/persistReducer';
import autoMergeLevel2 from 'redux-persist/es/stateReconciler/autoMergeLevel2';
import storage from 'redux-persist/es/storage';
import createSagaMiddleware from 'redux-saga';
import { captureError } from 'utils/helpers/errors';
import * as authTypes from './modules/auth/types';
import reducer, { persistBlacklist, RootState } from './reducer';
import rootSaga from './sagas';
import { createReduxEnhancer } from '@sentry/react';
import { configureStore, Tuple } from '@reduxjs/toolkit';
import DevTools from './DevTools';

// Thanks to https://blog.reactnativecoach.com/the-definitive-guide-to-redux-persist-84738167975
// for the persistance state process

export const persistConfig: PersistConfig<RootState> = {
  key: 'root',
  storage,
  blacklist: persistBlacklist,
  // The blacklist only stops future writes; copies of these slices already stored under the root key
  // (e.g. an auth slice saved mid-login) must not override what their own persist config restored
  stateReconciler: (inbound, original, reduced, config) => persistBlacklist.reduce((state, key) => ({
    ...state,
    [key]: reduced[key]
  }), autoMergeLevel2(inbound, original, reduced, config))
};
const sentryReduxConfig = {
  stateSanitizer: (state: RootState) => ({
    ...state,
    form: 'sanitized',
    account: {
      ...state.account,
      name: 'sanitized',
      email: 'sanitized',
      internalId: 'sanitized',
      phoneNumber: 'sanitized',
      stripeCustomerId: 'sanitized',
      streetNumber: 'sanitized',
      street: 'sanitized',
      address: 'sanitized'
    }
  }),
  actionSanitizer: (action: AnyAction) => {
    if (action.type === authTypes.LOGIN_REQUEST) {
      return {
        ...action,
        auth: {
          ...action.auth,
          password: 'sanitized'
        }
      };
    }
    if (action.type === authTypes.CREATE_PASSWORD_REQUEST) {
      return {
        ...action,
        values: {
          ...action.values,
          auth: {
            ...action.auth,
            password: 'sanitized'
          }
        }
      };
    }
    return action;
  }
};
const sentryReduxEnhancer = createReduxEnhancer({
  actionTransformer: sentryReduxConfig.actionSanitizer,
  stateTransformer: sentryReduxConfig.stateSanitizer
});
export default (initialState: Partial<RootState> & PersistPartial = {
  _persist: {
    version: -1,
    rehydrated: false
  }
}) => {
  // ======================================================
  // Middleware Configuration
  // ======================================================
  const sagaMiddleware = createSagaMiddleware({
    onError: (error, errorInfo) => captureError(error, {
      extra: {
        errorInfo
      }
    })
  });
  const middleware = [sagaMiddleware];

  // ======================================================
  // Store Enhancers
  // ======================================================

  // ======================================================
  // Store Instantiation and HMR Setup
  // ======================================================
  const store = configureStore({
    reducer: persistReducer<RootState>(persistConfig, reducer),
    preloadedState: initialState,
    middleware: getDefaultMiddleware => new Tuple(...getDefaultMiddleware({
      // Since we integrate with existing state not created with redux toolkit the serializableCheck
      // would fail in many cases and it can lead to unexpected behavior.
      serializableCheck: false
    }), ...middleware),
    // Disable RTK's built-in DevTools to avoid conflict with custom DevTools.instrument()
    // The custom DevTools (DockMonitor + LogMonitor) provides an in-page panel (Ctrl+H)
    // which is different from the browser extension that RTK enables by default.
    devTools: false,
    enhancers: getDefaultEnhancers => {
      const enhancers = getDefaultEnhancers().concat(sentryReduxEnhancer);
      // Add custom DevTools enhancer only in development
      if (process.env.NODE_ENV === 'development') {
        return enhancers.concat(DevTools.instrument());
      }
      return enhancers;
    }
  });
  sagaMiddleware.run(rootSaga);
  return store;
};